import { db } from './db';
import { listDocuments } from './library';
// Static: `account` already reaches the main chunk via AccountDialog, so a
// dynamic import here only produced a Rollup warning.
import { exportAllDocuments } from './account';
import { slugify } from './export/download';

/**
 * Automatic local backups. The biggest risk for a local-first app is silent
 * loss of the IndexedDB library (browser eviction, profile reset). This writes
 * every document as a plain `.md` file into a user-chosen folder via the File
 * System Access API, on a schedule, so there is always an off-app copy on disk.
 *
 * The chosen directory handle is persisted in IndexedDB (`kv`) and re-permissioned
 * on demand. Browsers without the API (Firefox/Safari/mobile) fall back to the
 * always-available "download a .zip backup".
 */

export type BackupCadence = 'off' | 'change' | 'daily';

export interface BackupSettings {
  cadence: BackupCadence;
  lastBackupAt: number | null;
  /** Display name of the chosen folder (the handle itself lives under DIR_KEY). */
  folderName: string | null;
}

const DEFAULTS: BackupSettings = { cadence: 'off', lastBackupAt: null, folderName: null };
const SETTINGS_KEY = 'backupSettings';
const DIR_KEY = 'backupDir';

interface DirPickerWindow {
  showDirectoryPicker?: (opts?: {
    mode?: 'read' | 'readwrite';
  }) => Promise<FileSystemDirectoryHandle>;
}

/** True where a folder can be chosen for automatic backups (Chromium desktop). */
export function supportsFolderBackup(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export async function getBackupSettings(): Promise<BackupSettings> {
  const row = await db.kv.get(SETTINGS_KEY);
  return { ...DEFAULTS, ...((row?.value as Partial<BackupSettings> | undefined) ?? {}) };
}

async function saveBackupSettings(patch: Partial<BackupSettings>): Promise<BackupSettings> {
  const next = { ...(await getBackupSettings()), ...patch };
  await db.kv.put({ key: SETTINGS_KEY, value: next });
  return next;
}

export function setBackupCadence(cadence: BackupCadence): Promise<BackupSettings> {
  return saveBackupSettings({ cadence });
}

async function getDirHandle(): Promise<FileSystemDirectoryHandle | null> {
  const row = await db.kv.get(DIR_KEY);
  return (row?.value as FileSystemDirectoryHandle | undefined) ?? null;
}

/** Prompt for a backup folder and remember it. Returns null if cancelled/unsupported. */
export async function chooseBackupFolder(): Promise<BackupSettings | null> {
  const pick = (window as DirPickerWindow).showDirectoryPicker;
  if (!pick) return null;
  try {
    const handle = await pick({ mode: 'readwrite' });
    await db.kv.put({ key: DIR_KEY, value: handle });
    return await saveBackupSettings({ folderName: handle.name });
  } catch {
    return null; // user cancelled
  }
}

interface PermissionedHandle {
  queryPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (o: { mode: 'readwrite' }) => Promise<PermissionState>;
}

async function ensureWritable(
  handle: FileSystemDirectoryHandle,
  prompt: boolean,
): Promise<boolean> {
  const h = handle as unknown as PermissionedHandle;
  const opts = { mode: 'readwrite' } as const;
  if ((await h.queryPermission?.(opts)) === 'granted') return true;
  if (!prompt) return false;
  return (await h.requestPermission?.(opts)) === 'granted';
}

export type BackupResult =
  { ok: true; count: number } | { ok: false; reason: 'no-folder' | 'permission' | 'error' };

/**
 * Write every live document as a `.md` file into the chosen folder. `prompt`
 * permits a permission dialog (only works inside a user gesture — automatic
 * runs pass `false` and silently skip if permission has lapsed).
 */
export async function runFolderBackup(prompt = false): Promise<BackupResult> {
  const handle = await getDirHandle();
  if (!handle) return { ok: false, reason: 'no-folder' };
  if (!(await ensureWritable(handle, prompt))) return { ok: false, reason: 'permission' };
  try {
    const docs = await listDocuments();
    const used = new Map<string, number>();
    for (const d of docs) {
      const base = slugify(d.title || 'untitled') || 'untitled';
      const n = (used.get(base) ?? 0) + 1;
      used.set(base, n);
      const name = `${n > 1 ? `${base}-${n}` : base}.md`;
      const fileHandle = await handle.getFileHandle(name, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(d.content);
      await writable.close();
    }
    await saveBackupSettings({ lastBackupAt: Date.now() });
    return { ok: true, count: docs.length };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/** Fallback backup: download the whole library as a `.zip` of `.md` files. */
export async function downloadBackupZip(): Promise<number> {
  return exportAllDocuments();
}
